import { Timestamp } from '@bufbuild/protobuf';
import { ImageWrapper, pxToRem, pxTovW, theme } from '@geneo2-web/shared-ui';
import {
  Box,
  Button,
  Modal,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  StudentOmrPerformanceInfo,
  StudentPerformanceInfo,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useState } from 'react';

type StudentPerformanceList = (
  | StudentPerformanceInfo
  | StudentOmrPerformanceInfo
)[];

const isStudentPerformanceInfo = (
  student: StudentPerformanceInfo | StudentOmrPerformanceInfo
): student is StudentPerformanceInfo => 'timeMismatches' in student;

interface ITimeMismatchButton {
  rootSx?: SxProps;
  studentPerformanceInfoList?: StudentPerformanceList;
}

export const formatDateTimeMismatch = (timestamp?: Timestamp) => {
  const date = timestamp ? new Timestamp(timestamp).toDate() : undefined;
  if (!date) return '';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const formatTimeMismatches = (student: StudentPerformanceInfo) =>
  student.timeMismatches
    ?.map((val) => {
      return formatDateTimeMismatch(val?.timestamp);
    })
    .filter(Boolean)
    .join(' | ') || '-';

const TimeMismatchButton = (props: ITimeMismatchButton) => {
  const { studentPerformanceInfoList, rootSx } = props;
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!studentPerformanceInfoList) {
    return null;
  }

  const filteredStudents = studentPerformanceInfoList.filter(
    (student): student is StudentPerformanceInfo =>
      isStudentPerformanceInfo(student) && !!student.timeMismatches?.length
  );

  if (!filteredStudents.length) {
    return null;
  }

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        sx={{
          cursor: 'pointer',
          border: '1px solid #158D29',
          borderRadius: pxToRem(10),
          color: '#0AA34F',
          display: 'flex',
          alignItems: 'center',
          gap: pxToRem(10),
          ...rootSx,
        }}
      >
        Time Mismatches
      </Button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'relative',
            margin: 'auto',
            marginTop: { xs: '20%', md: '10%' },
            width: { xs: '95%', md: pxTovW(600) },
            maxHeight: { xs: '70vh', md: '80vh' },
            backgroundColor: 'common.white',
            borderRadius: { xs: pxToRem(10), md: pxTovW(15) },
            padding: { xs: pxToRem(16), md: pxTovW(24) },
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: pxToRem(12), md: pxTovW(16) },
            outline: 'none',
          }}
        >
          <Typography variant="h3" fontWeight="bold">
            Time
          </Typography>

          <TableContainer sx={{ overflowY: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f4f9fd' }}>
                  <TableCell
                    sx={{
                      borderBottom: '1px solid #007CDC',
                      py: { xs: pxToRem(8), md: pxTovW(12) },
                    }}
                  >
                    <Typography variant="h4" fontWeight="bold">
                      Name
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: '1px solid #007CDC',
                      py: { xs: pxToRem(8), md: pxTovW(12) },
                    }}
                  >
                    <Typography variant="h4" fontWeight="bold">
                      Time Mismatches
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStudents.map((student, index) => (
                  <TableRow
                    key={student.studentId.toString()}
                    sx={{
                      backgroundColor: index % 2 === 1 ? '#f4f9fd' : 'white',
                    }}
                  >
                    <TableCell sx={{ py: { xs: pxToRem(8), md: pxTovW(12) } }}>
                      <Typography variant="body2" color="primary">
                        {student.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: { xs: pxToRem(8), md: pxTovW(12) } }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatTimeMismatches(student)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!isMobile && (
            <ImageWrapper
              name="close-yellow"
              type="png"
              parentFolder="icons"
              onClick={() => setOpen(false)}
              styles={{
                height: { xs: pxToRem(50), md: pxTovW(50) },
                width: { xs: pxToRem(50), md: pxTovW(50) },
                position: 'absolute',
                top: pxTovW(20),
                right: pxTovW(-60),
                cursor: 'pointer',
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default TimeMismatchButton;
